import { type FC, useMemo } from 'react';
import { RiSquareFill } from 'react-icons/ri';
import { type StudyApiId } from '@/model/Study.model';
import { _isNil, _notNil } from '@/littledash';
import {
  type GroupMap,
  type SampleTypeMap,
  type MetaField,
  type ReservedSample,
  type SampleDefinitionMap,
} from './SampleNames.model';
import { capitalise, renderTimepoint, getMetaValue } from './SampleNames.utils';
import { type GroupApiId, type TreatmentGroup } from '@/model/TreatmentGroup.model';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import { RouteService } from '@/support/RouteService';
import styles from './SampleNamesTable.module.scss';

const GroupChip: FC<{ group: TreatmentGroup | undefined }> = ({ group }) => {
  if (_isNil(group)) {
    return null;
  }
  return (
    <div className="flex flex-row items-center" style={{ gap: '0.25rem' }}>
      {_notNil(group.color) && <RiSquareFill color={group.color} />}
      <span>{group.name}</span>
    </div>
  );
};

/**
 * Flat (ungrouped) view of all reserved samples for a study, rendered as a paginated table.
 * Receives a pre-built sampleDefinitionMap from the parent to avoid a duplicate network request.
 */
interface ReservedSamplesFlatTableProps {
  studyApiId: StudyApiId;
  groupMap: GroupMap;
  sampleTypeMap: SampleTypeMap;
  metaFields: Array<MetaField>;
  sampleDefinitionMap: SampleDefinitionMap;
}

const ReservedSamplesFlatTable: FC<ReservedSamplesFlatTableProps> = ({
  studyApiId,
  groupMap,
  sampleTypeMap,
  metaFields,
  sampleDefinitionMap,
}) => {
  const columns = useMemo<Array<TanStackColumnDef<ReservedSample>>>(() => {
    const staticColumns: Array<TanStackColumnDef<ReservedSample>> = [
      {
        id: 'name',
        header: 'Sample Name',
        accessorKey: 'name',
        enableSorting: false,
        enableResizing: true,
        size: 200,
      },
      {
        id: 'sampleDefinition',
        header: 'Sample Definition',
        accessorFn: (row) => row.sample_definition_api_id,
        cell: ({ row }) => {
          const defApiId = row.original.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          return sampleDefinitionMap.get(defApiId)?.name ?? defApiId;
        },
        enableSorting: false,
        enableResizing: true,
        size: 200,
      },
      {
        id: 'group',
        header: 'Study Group',
        accessorFn: (row) => row.study_group_api_id,
        cell: ({ row }) => {
          const group = _notNil(row.original.study_group_api_id)
            ? groupMap.get(row.original.study_group_api_id as GroupApiId)
            : undefined;
          return <GroupChip group={group} />;
        },
        enableSorting: false,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'plannedAnimal',
        header: 'Planned Animal',
        accessorFn: (row) => row.planned_animal_id ?? '',
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'animal',
        header: 'Animal',
        accessorFn: (row) => row.animal_name ?? '',
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => capitalise(row.status),
        enableSorting: false,
        enableResizing: true,
        size: 120,
      },
      {
        id: 'timepoint',
        header: 'Timepoint',
        accessorFn: (row) => row.timepoint ?? '',
        cell: ({ row }) => (_notNil(row.original.timepoint) ? renderTimepoint(row.original.timepoint) : ''),
        enableSorting: false,
        enableResizing: true,
        size: 160,
      },
      {
        id: 'sampleType',
        header: 'Sample Type',
        accessorFn: (row) => {
          const defApiId = row.sample_definition_api_id;
          if (_isNil(defApiId)) {
            return '';
          }
          const sampleDef = sampleDefinitionMap.get(defApiId);
          return _notNil(sampleDef) ? (sampleTypeMap.get(sampleDef.sample_type) ?? sampleDef.sample_type) : '';
        },
        enableSorting: false,
        enableResizing: true,
        size: 140,
      },
    ];

    const metaColumns = metaFields.map<TanStackColumnDef<ReservedSample>>((field) => ({
      id: `meta_${field.apiId}`,
      header: field.title,
      accessorFn: (row) => {
        const defApiId = row.sample_definition_api_id;
        if (_isNil(defApiId)) {
          return '';
        }
        const sampleDef = sampleDefinitionMap.get(defApiId);
        return _notNil(sampleDef) ? getMetaValue(sampleDef.metadata, field.apiId) : '';
      },
      enableSorting: false,
      enableResizing: true,
      enableHiding: true,
      isVisible: false,
      size: 160,
    }));

    return [...staticColumns, ...metaColumns];
  }, [sampleDefinitionMap, groupMap, sampleTypeMap, metaFields]);

  const apiInfo = useMemo(
    () => ({
      type: 'internalApi' as const,
      route: RouteService.api({
        endpoint: 'GET /api/v1/studies/{studyApiId}/planned/samples/reserved-samples',
        path: { studyApiId },
      }).url,
    }),
    [studyApiId]
  );

  return (
    <div
      className={styles.FlatTableContainer}
      data-test-component="ReservedSamplesFlatTable"
      data-test-element="container"
    >
      <APITable<ReservedSample>
        columns={columns}
        apiInfo={apiInfo}
        reduxTableName="reservedSamples"
        searchPlaceholderText="Search samples"
        searchQueryByOptions={[
          { value: 'name', display: 'Name' },
          { value: 'animal', display: 'Animal' },
          { value: 'planned_animal', display: 'Planned Animal' },
        ]}
        hideFilter
        defaultPageSize={1000}
        noDataMessage="Reserved sample names will appear here once generated"
        testIdPrefix="reserved-samples-flat"
      />
    </div>
  );
};

export default ReservedSamplesFlatTable;
